/**
 * Scope-hammering detection.
 *
 * Shape Up convention: a milestone the team has decided to cut from the
 * project's scope to defend the appetite is prefixed (or tagged anywhere
 * in its name) with `[SH]`. This file gives the rest of the codebase a
 * single source of truth for "is this dot hammered?".
 *
 * Why a substring match (not strict prefix):
 * - Teams sometimes type a leading space, a trailing tag, or a casing
 *   variant (`[sh]`, `[SH] `, ` [SH]`). All of these mean the same thing
 *   semantically, and refusing to detect them would be a usability trap.
 * - The match is case-insensitive for the same reason.
 *
 * Why derived (not stored):
 * - A snapshot froze the dot's name at capture time. Computing the hammered
 *   state from that frozen name automatically gives us the correct visual
 *   per snapshot — a snapshot taken before `[SH]` was added renders the dot
 *   normally; a snapshot taken after renders it greyed-out with the X
 *   overlay. No extra column, no backfill, no drift risk.
 * - Un-hammering is symmetric: removing `[SH]` from the upstream scope name
 *   flows through the resync update path, the new name replaces the old one
 *   in the live dot, and `isDotHammered` returns false again. The dot regains
 *   its original color and movability with zero extra state to clean up.
 */

const SH_PATTERN = /\[sh\]/i;

/**
 * True when the dot's display name carries the scope-hammering tag.
 * Pure, branchless on the call site, safe to call inside render loops.
 */
export function isDotHammered(externalScopeName: string): boolean {
  return SH_PATTERN.test(externalScopeName);
}
